/* ── useInsightsData ─────────────────────────────────────────────────
   All state, data-loading callbacks, derived memos, and selection logic
   for the PrivilegeAccessInsights page.  The page component itself only
   needs to destructure the return value and render JSX. */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  fetchPagAccountDetails,
  fetchPagAccountFilters,
  fetchPagAccountList,
  fetchPagInventoryBreakdown,
  fetchPagPamCoverageSummary,
  fetchPagPrivilegedAccountBreakdown,
  fetchPagPrivilegedGroupChart,
  fetchPagRiskChart,
  fetchPagSourceSystemFilter,
  submitPagReviewAction,
  type PagAccountDetails,
  type PagAccountFiltersResponse,
  type PagAccountRow,
  type PagInventoryBreakdown,
  type PagPamCoverageSummary,
  type PagPrivilegedAccountBreakdown,
  type PagPrivilegedGroupChart,
  type PagRiskChart,
  type PagSourceSystemFilter,
} from "../services/pagApi";
import { useAuthStore } from "../../../../stores/authStore";
import { useToastStore } from "../../../../stores/toastStore";
import { MAX_PAGE_SIZE } from "../../../../utils/formatters";
import {
  GROUP_CHART_COLORS,
  REVIEW_STATUS_FILTERS_HIDDEN,
  RISK_COLORS,
  reviewStatusLabel,
} from "../constants/pagConstants";
import type { ChartDatum } from "../constants/pagInsightsConstants";
import {
  getAccountDueDate,
  getResourceValuesForSystems,
  hasOpenReviewRequest,
  pagErrorMessage,
  resourceOptions,
  sortAccountsByGroup,
  sourceSystemOptions,
} from "../helpers/pagHelpers";
import { cycleSortDir } from "../../../components/DataTable";
import type { PagChartDrilldown } from "../components/PagChartDrilldownDrawer";
import type { AccessAction, AccessActionPayload } from "../AccessActionDialog";

const normalizeSourceSystemIds = (values: Array<string | number>): number[] =>
  Array.from(
    new Set(
      values
        .map(Number)
        .filter((id) => Number.isSafeInteger(id) && id > 0),
    ),
  );

export const useInsightsData = (options?: { accountsOnly?: boolean }) => {
  const accountsOnly = options?.accountsOnly ?? false;
  /* ── Source system / scope ─────────────────────────────────────── */
  const [sourceSystems, setSourceSystems] = useState<string[]>([]);
  const [resources, setResources] = useState<string[]>([]);
  const [sourceSystemFilters, setSourceSystemFilters] = useState<PagSourceSystemFilter[]>([]);
  /* The dropdowns only stage a choice. Filter is what applies it, so nothing
     re-reads until it is pressed. */
  const [appliedSourceSystemIds, setAppliedSourceSystemIds] = useState<number[]>([]);
  const [reloadToken, setReloadToken] = useState(0);
  /* The list endpoints take every selected instance; the ones that take a
     single id use the first. */
  const sourceSystemId = appliedSourceSystemIds[0] ?? 0;
  const stagedSourceSystemIds = useMemo(
    () => normalizeSourceSystemIds(resources),
    [resources],
  );

  /* ── Breakdown / chart state ───────────────────────────────────── */
  const [inventoryPageSize, setInventoryPageSize] = useState(MAX_PAGE_SIZE);
  const [privilegedPageSize, setPrivilegedPageSize] = useState(MAX_PAGE_SIZE);
  const inventoryPageSizeRef = useRef(MAX_PAGE_SIZE);
  inventoryPageSizeRef.current = inventoryPageSize;
  const privilegedPageSizeRef = useRef(MAX_PAGE_SIZE);
  privilegedPageSizeRef.current = privilegedPageSize;

  const [inventorySortKey, setInventorySortKey] = useState<string>("accountCount");
  const [inventorySortDir, setInventorySortDir] = useState<"asc" | "desc">("desc");
  const inventorySortKeyRef = useRef("accountCount");
  inventorySortKeyRef.current = inventorySortKey;
  const inventorySortDirRef = useRef<"asc" | "desc">("desc");
  inventorySortDirRef.current = inventorySortDir;

  const [privilegedSortKey, setPrivilegedSortKey] = useState<string>("privilegedAccountCount");
  const [privilegedSortDir, setPrivilegedSortDir] = useState<"asc" | "desc">("desc");
  const privilegedSortKeyRef = useRef("privilegedAccountCount");
  privilegedSortKeyRef.current = privilegedSortKey;
  const privilegedSortDirRef = useRef<"asc" | "desc">("desc");
  privilegedSortDirRef.current = privilegedSortDir;

  const [breakdown, setBreakdown] = useState<PagInventoryBreakdown | null>(null);
  const [inventoryPage, setInventoryPage] = useState(0);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  const [privilegedAccountBreakdown, setPrivilegedAccountBreakdown] =
    useState<PagPrivilegedAccountBreakdown | null>(null);
  const [privilegedPage, setPrivilegedPage] = useState(0);
  const [privilegedLoading, setPrivilegedLoading] = useState(false);

  const [pamCoverage, setPamCoverage] = useState<PagPamCoverageSummary | null>(null);
  const [riskChart, setRiskChart] = useState<PagRiskChart | null>(null);
  const [groupChart, setGroupChart] = useState<PagPrivilegedGroupChart | null>(null);
  const [inventorySearch, setInventorySearch] = useState("");
  const [inventorySearching, setInventorySearching] = useState(false);
  const inventorySearchRef = useRef("");
  const inventorySearchSeq = useRef(0);
  const [privilegedSearch, setPrivilegedSearch] = useState("");
  const [privilegedSearching, setPrivilegedSearching] = useState(false);
  const privilegedSearchRef = useRef("");
  const privilegedSearchSeq = useRef(0);
  const [insightsLoading, setInsightsLoading] = useState(false);
  const breakdownRequestRef = useRef(0);

  /* ── Breakdown loader ──────────────────────────────────── */
  const loadBreakdown = useCallback(async () => {
    if (accountsOnly || !appliedSourceSystemIds.length) {
      setInsightsLoading(false);
      setBreakdown(null);
      setPrivilegedAccountBreakdown(null);
      setPamCoverage(null);
      setRiskChart(null);
      setGroupChart(null);
      return;
    }
    const requestId = ++breakdownRequestRef.current;
    const scopeIds = normalizeSourceSystemIds(appliedSourceSystemIds);
    setInsightsLoading(true);
    setInventoryPage(0);
    setPrivilegedPage(0);
    try {
      const [invResult, privResult, pamResult, riskResult, groupResult] = await Promise.allSettled([
        fetchPagInventoryBreakdown(
          scopeIds,
          0,
          inventoryPageSizeRef.current,
          inventorySearchRef.current,
          inventorySortKeyRef.current,
          inventorySortDirRef.current,
        ),
        fetchPagPrivilegedAccountBreakdown(
          scopeIds,
          0,
          privilegedPageSizeRef.current,
          privilegedSearchRef.current,
          privilegedSortKeyRef.current,
          privilegedSortDirRef.current,
        ),
        fetchPagPamCoverageSummary(scopeIds),
        fetchPagRiskChart(scopeIds),
        fetchPagPrivilegedGroupChart(scopeIds),
      ]);

      if (requestId !== breakdownRequestRef.current) return;
      if (invResult.status === "fulfilled" && invResult.value?.groups) {
        setBreakdown(invResult.value);
      }
      if (privResult.status === "fulfilled" && privResult.value?.groups) {
        setPrivilegedAccountBreakdown(privResult.value);
      }
      if (pamResult.status === "fulfilled" && pamResult.value) {
        setPamCoverage(pamResult.value);
      }
      if (riskResult.status === "fulfilled" && riskResult.value) {
        setRiskChart(riskResult.value);
      }
      if (groupResult.status === "fulfilled" && groupResult.value) {
        setGroupChart(groupResult.value);
      }
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Could not load the account breakdown",
          pagErrorMessage(err, "Please retry in a moment."),
        );
    } finally {
      if (requestId === breakdownRequestRef.current) setInsightsLoading(false);
    }
  }, [sourceSystemId, appliedSourceSystemIds, reloadToken]);

  useEffect(() => {
    void loadBreakdown();
  }, [loadBreakdown]);

  /* ── Pagination: inventory ──────────────────────────────────────── */
  const changeInventoryPage = useCallback(
    async (nextPage: number) => {
      setInventoryPage(nextPage);
      setInventoryLoading(true);
      try {
        const result = await fetchPagInventoryBreakdown(
          appliedSourceSystemIds,
          nextPage,
          inventoryPageSizeRef.current,
          inventorySearchRef.current,
          inventorySortKeyRef.current,
          inventorySortDirRef.current,
        );
        if (result?.groups) {
          setBreakdown(result);
        }
      } catch (err: unknown) {
        useToastStore
          .getState()
          .showError(
            "Could not load the inventory page",
            pagErrorMessage(err, "Please retry in a moment."),
          );
      } finally {
        setInventoryLoading(false);
      }
    },
    [appliedSourceSystemIds],
  );

  const changeInventoryPageSize = useCallback(
    async (nextSize: number) => {
      setInventoryPageSize(nextSize);
      inventoryPageSizeRef.current = nextSize;
      setInventoryPage(0);
      setInventoryLoading(true);
      try {
        const result = await fetchPagInventoryBreakdown(
          appliedSourceSystemIds,
          0,
          nextSize,
          inventorySearchRef.current,
          inventorySortKeyRef.current,
          inventorySortDirRef.current,
        );
        if (result?.groups) {
          setBreakdown(result);
        }
      } catch (err: unknown) {
        useToastStore
          .getState()
          .showError(
            "Could not update inventory page size",
            pagErrorMessage(err, "Please retry in a moment."),
          );
      } finally {
        setInventoryLoading(false);
      }
    },
    [appliedSourceSystemIds],
  );

  /* ── Pagination: privileged ─────────────────────────────────────── */
  const changePrivilegedPage = useCallback(
    async (nextPage: number) => {
      setPrivilegedPage(nextPage);
      setPrivilegedLoading(true);
      try {
        const result = await fetchPagPrivilegedAccountBreakdown(
          appliedSourceSystemIds,
          nextPage,
          privilegedPageSizeRef.current,
          privilegedSearchRef.current,
          privilegedSortKeyRef.current,
          privilegedSortDirRef.current,
        );
        if (result?.groups) {
          setPrivilegedAccountBreakdown(result);
        }
      } catch (err: unknown) {
        useToastStore
          .getState()
          .showError(
            "Could not load the privileged breakdown page",
            pagErrorMessage(err, "Please retry in a moment."),
          );
      } finally {
        setPrivilegedLoading(false);
      }
    },
    [appliedSourceSystemIds],
  );

  const changePrivilegedPageSize = useCallback(
    async (nextSize: number) => {
      setPrivilegedPageSize(nextSize);
      privilegedPageSizeRef.current = nextSize;
      setPrivilegedPage(0);
      setPrivilegedLoading(true);
      try {
        const result = await fetchPagPrivilegedAccountBreakdown(
          appliedSourceSystemIds,
          0,
          nextSize,
          privilegedSearchRef.current,
          privilegedSortKeyRef.current,
          privilegedSortDirRef.current,
        );
        if (result?.groups) {
          setPrivilegedAccountBreakdown(result);
        }
      } catch (err: unknown) {
        useToastStore
          .getState()
          .showError(
            "Could not update privileged page size",
            pagErrorMessage(err, "Please retry in a moment."),
          );
      } finally {
        setPrivilegedLoading(false);
      }
    },
    [appliedSourceSystemIds],
  );

  /* ── Search: inventory ─────────────────────────────────────────── */
  const searchInventory = useCallback(
    async (term: string) => {
      const seq = ++inventorySearchSeq.current;
      inventorySearchRef.current = term;
      setInventorySearching(true);
      setInventoryPage(0);
      try {
        const result = await fetchPagInventoryBreakdown(
          appliedSourceSystemIds,
          0,
          inventoryPageSizeRef.current,
          term,
          inventorySortKeyRef.current,
          inventorySortDirRef.current,
        );
        if (seq !== inventorySearchSeq.current) return;
        if (result) setBreakdown(result);
      } catch (err: unknown) {
        if (seq !== inventorySearchSeq.current) return;
        useToastStore
          .getState()
          .showError(
            "Could not search the account breakdown",
            pagErrorMessage(err, "Please retry in a moment."),
          );
      } finally {
        if (seq === inventorySearchSeq.current) setInventorySearching(false);
      }
    },
    [appliedSourceSystemIds],
  );

  /* ── Search: privileged ────────────────────────────────────────── */
  const searchPrivileged = useCallback(
    async (term: string) => {
      const seq = ++privilegedSearchSeq.current;
      privilegedSearchRef.current = term;
      setPrivilegedSearching(true);
      setPrivilegedPage(0);
      try {
        const result = await fetchPagPrivilegedAccountBreakdown(
          appliedSourceSystemIds,
          0,
          privilegedPageSizeRef.current,
          term,
          privilegedSortKeyRef.current,
          privilegedSortDirRef.current,
        );
        if (seq !== privilegedSearchSeq.current) return;
        if (result) setPrivilegedAccountBreakdown(result);
      } catch (err: unknown) {
        if (seq !== privilegedSearchSeq.current) return;
        useToastStore
          .getState()
          .showError(
            "Could not search the privileged breakdown",
            pagErrorMessage(err, "Please retry in a moment."),
          );
      } finally {
        if (seq === privilegedSearchSeq.current) setPrivilegedSearching(false);
      }
    },
    [appliedSourceSystemIds],
  );

  /* ── Breakdown sorting (calls API with payload) ─────────────────── */
  const changeInventorySort = useCallback(
    async (key: string) => {
      const nextDir: "asc" | "desc" =
        inventorySortKeyRef.current === key && inventorySortDirRef.current === "desc"
          ? "asc"
          : "desc";
      setInventorySortKey(key);
      setInventorySortDir(nextDir);
      inventorySortKeyRef.current = key;
      inventorySortDirRef.current = nextDir;
      setInventoryPage(0);
      setInventoryLoading(true);
      try {
        const result = await fetchPagInventoryBreakdown(
          appliedSourceSystemIds,
          0,
          inventoryPageSizeRef.current,
          inventorySearchRef.current,
          key,
          nextDir,
        );
        if (result?.groups) {
          setBreakdown(result);
        }
      } catch (err: unknown) {
        useToastStore
          .getState()
          .showError(
            "Could not sort account breakdown",
            pagErrorMessage(err, "Please retry in a moment."),
          );
      } finally {
        setInventoryLoading(false);
      }
    },
    [appliedSourceSystemIds],
  );

  const changePrivilegedSort = useCallback(
    async (key: string) => {
      const nextDir: "asc" | "desc" =
        privilegedSortKeyRef.current === key && privilegedSortDirRef.current === "desc"
          ? "asc"
          : "desc";
      setPrivilegedSortKey(key);
      setPrivilegedSortDir(nextDir);
      privilegedSortKeyRef.current = key;
      privilegedSortDirRef.current = nextDir;
      setPrivilegedPage(0);
      setPrivilegedLoading(true);
      try {
        const result = await fetchPagPrivilegedAccountBreakdown(
          appliedSourceSystemIds,
          0,
          privilegedPageSizeRef.current,
          privilegedSearchRef.current,
          key,
          nextDir,
        );
        if (result?.groups) {
          setPrivilegedAccountBreakdown(result);
        }
      } catch (err: unknown) {
        useToastStore
          .getState()
          .showError(
            "Could not sort privileged breakdown",
            pagErrorMessage(err, "Please retry in a moment."),
          );
      } finally {
        setPrivilegedLoading(false);
      }
    },
    [appliedSourceSystemIds],
  );

  /* ── Derived chart data ────────────────────────────────────────── */
  const inventoryGroups = useMemo(() => {
    const list = [...(breakdown?.groups?.content ?? [])];
    list.sort((a, b) => {
      let comparison = 0;
      switch (inventorySortKey) {
        case "groupName":
          comparison = (a.groupName ?? "").localeCompare(b.groupName ?? "");
          break;
        case "source": {
          const aSource = a.sourceSystemName ?? a.sourceSystemType ?? "";
          const bSource = b.sourceSystemName ?? b.sourceSystemType ?? "";
          comparison = aSource.localeCompare(bSource);
          break;
        }
        case "description":
          comparison = (a.description ?? "").localeCompare(b.description ?? "");
          break;
        case "percentage":
          comparison = (a.percentage ?? 0) - (b.percentage ?? 0);
          break;
        case "accountCount":
        default:
          comparison = (a.accountCount ?? 0) - (b.accountCount ?? 0);
          break;
      }
      return inventorySortDir === "asc" ? comparison : -comparison;
    });
    return list;
  }, [breakdown, inventorySortKey, inventorySortDir]);

  const privilegedGroups = useMemo(() => {
    const list = [...(privilegedAccountBreakdown?.groups?.content ?? [])];
    list.sort((a, b) => {
      let comparison = 0;
      switch (privilegedSortKey) {
        case "groupName":
          comparison = (a.groupName ?? "").localeCompare(b.groupName ?? "");
          break;
        case "source": {
          const aSource = a.sourceSystemName ?? a.sourceSystemType ?? "";
          const bSource = b.sourceSystemName ?? b.sourceSystemType ?? "";
          comparison = aSource.localeCompare(bSource);
          break;
        }
        case "description":
          comparison = (a.description ?? "").localeCompare(b.description ?? "");
          break;
        case "pamCoveredCount":
          comparison = (a.pamCoveredCount ?? 0) - (b.pamCoveredCount ?? 0);
          break;
        case "pamCoveragePercentage":
          comparison = (a.pamCoveragePercentage ?? 0) - (b.pamCoveragePercentage ?? 0);
          break;
        case "privilegedAccountCount":
        default:
          comparison = (a.privilegedAccountCount ?? 0) - (b.privilegedAccountCount ?? 0);
          break;
      }
      return privilegedSortDir === "asc" ? comparison : -comparison;
    });
    return list;
  }, [privilegedAccountBreakdown, privilegedSortKey, privilegedSortDir]);

  const accountsByGroup = useMemo(() => {
    const list = groupChart?.groups ?? [];
    if (list.length > 0) {
      return list.map((group, index) => ({
        name: group.groupName,
        value: group.accountCount ?? 0,
        color: GROUP_CHART_COLORS[index % GROUP_CHART_COLORS.length],
        key: group.groupName,
      }));
    }
    return (privilegedAccountBreakdown?.groups?.content ?? []).map((group, index) => ({
      name: group.groupName,
      value: group.privilegedAccountCount ?? 0,
      color: GROUP_CHART_COLORS[index % GROUP_CHART_COLORS.length],
      key: group.groupName,
    }));
  }, [groupChart, privilegedAccountBreakdown]);

  const riskDistribution = useMemo(() => {
    const slices = riskChart?.slices ?? [];
    const ordered = [
      ...slices.filter((slice) => slice.riskLevel === "UNCATEGORISED"),
      ...slices.filter((slice) => slice.riskLevel !== "UNCATEGORISED"),
    ];
    return ordered.map((slice) => ({
      name:
        slice.riskLevel === "UNCATEGORISED" ? "Unassigned" : (slice.label ?? slice.riskLevel),
      value: slice.count ?? 0,
      color: RISK_COLORS[slice.riskLevel] ?? "#8b949e",
      key: slice.riskLevel,
    }));
  }, [riskChart]);

  const pamDistribution = useMemo(
    () => [
      {
        name: "Vaulted",
        value: pamCoverage?.pamCoveredAccounts ?? 0,
        color: "#22c55e",
        key: "Vaulted",
      },
      {
        name: "Not Vaulted",
        value: pamCoverage?.uncoveredAccounts ?? 0,
        color: "#ef4444",
        key: "Not Vaulted",
      },
    ],
    [pamCoverage],
  );

  const inventoryTotal = breakdown?.totalAccounts ?? 0;
  const privilegedTotal = breakdown?.privilegedCount ?? 0;
  const privilegedPct = (breakdown?.privilegedPercentage ?? 0).toFixed(2);

  /* ── Source system filter init ─────────────────────────────────── */
  const initialSelectionAppliedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetchPagSourceSystemFilter()
      .then((systems) => {
        if (cancelled) return;
        const list = Array.isArray(systems) ? systems : [];
        setSourceSystemFilters(list);
        if (!initialSelectionAppliedRef.current && list.length > 0) {
          initialSelectionAppliedRef.current = true;
          const allSystems = sourceSystemOptions(list)
            .map((opt) => opt.value)
            .filter(Boolean);
          const allResources = resourceOptions(list, allSystems).map((opt) => opt.value);
          const allIds = normalizeSourceSystemIds(allResources);
          setSourceSystems(allSystems);
          setResources(allResources);
          if (allIds.length > 0) {
            setAppliedSourceSystemIds(allIds);
          }
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        useToastStore
          .getState()
          .showError(
            "Could not load source systems",
            pagErrorMessage(err, "Please retry in a moment."),
          );
      });
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Account list state ────────────────────────────────────────── */
  const [search, setSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");

  const submitSearch = useCallback((value?: string) => {
    const term = (typeof value === "string" ? value : search).trim();
    setAppliedSearch(term);
    setPage(0);
  }, [search]);

  const handleSearchChange = useCallback((value: string) => {
    setSearch(value);
    if (!value.trim()) {
      setAppliedSearch("");
      setPage(0);
    }
  }, []);
  const [pamFilters, setPamFilters] = useState<string[]>([]);
  const [reviewFilters, setReviewFilters] = useState<string[]>([]);
  const [riskFilters, setRiskFilters] = useState<string[]>([]);
  const [groupFilters, setGroupFilters] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(MAX_PAGE_SIZE);
  const [sortKey, setSortKey] = useState<string | null>("accountName");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");

  const [accounts, setAccounts] = useState<PagAccountRow[]>([]);
  const [accountsTotal, setAccountsTotal] = useState(0);
  const [accountsLoading, setAccountsLoading] = useState(false);
  const [scopeLoading, setScopeLoading] = useState(false);
  const accountsRequestRef = useRef(0);
  const prevScopeRef = useRef<{ ids: number[]; token: number }>({
    ids: appliedSourceSystemIds,
    token: reloadToken,
  });
  const [accessAction, setAccessAction] = useState<AccessAction | null>(null);
  const [actionNames, setActionNames] = useState<string[]>([]);
  const [submittingAction, setSubmittingAction] = useState(false);
  const [detailAccount, setDetailAccount] = useState<PagAccountRow | null>(null);
  const [detailData, setDetailData] = useState<PagAccountDetails | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [drilldown, setDrilldown] = useState<PagChartDrilldown | null>(null);

  /* ── Action handlers ───────────────────────────────────────────── */
  const openAccessAction = useCallback((action: AccessAction, accountNames: string[]) => {
    if (!accountNames.length) return;
    setActionNames(accountNames);
    setAccessAction(action);
  }, []);

  const openDrilldown = useCallback(
    (
      scope: PagChartDrilldown["scope"],
      chartTitle: string,
      datum: ChartDatum,
      sourceSystemIds?: number[],
    ) => {
      if (!datum?.value) return;
      if (
        datum.name?.toLowerCase().includes("not in any group") ||
        datum.key?.toLowerCase().includes("not in any group")
      ) {
        return;
      }
      setDrilldown({
        scope,
        value: datum.key ?? datum.name,
        label: datum.name,
        chartTitle,
        count: datum.value,
        sourceSystemIds: sourceSystemIds ?? datum.sourceSystemIds,
      });
    },
    [],
  );

  /* ── Account list loader ───────────────────────────────────────── */
  const loadAccounts = useCallback(async () => {
    if (!appliedSourceSystemIds.length) {
      setAccounts([]);
      setAccountsTotal(0);
      setAccountsLoading(false);
      setScopeLoading(false);
      return;
    }
    const requestId = ++accountsRequestRef.current;
    const scopeIds = normalizeSourceSystemIds(appliedSourceSystemIds);
    const isScopeChange =
      prevScopeRef.current.token !== reloadToken ||
      prevScopeRef.current.ids !== appliedSourceSystemIds;
    if (isScopeChange) {
      prevScopeRef.current = { ids: appliedSourceSystemIds, token: reloadToken };
      setScopeLoading(true);
    }
    setAccountsLoading(true);
    try {
      const result = await fetchPagAccountList(
        {
          sourceSystemIds: scopeIds,
          search: appliedSearch || null,
          sortBy: sortKey ?? "accountName",
          sortDir,
          ...(riskFilters.length ? { riskLevels: riskFilters } : {}),
          ...(pamFilters.length ? { pamStatuses: pamFilters } : {}),
          ...(groupFilters.length
            ? {
                groupNames: groupFilters,
                ...(groupFilters.length === 1 ? { groupName: groupFilters[0] } : {}),
              }
            : {}),
          ...(reviewFilters.length ? { reviewStatuses: reviewFilters } : {}),
        },
        page,
        pageSize,

      );
      const content = result?.content ?? [];
      if (requestId !== accountsRequestRef.current) return;
      setAccounts(content);
      setAccountsTotal(result?.totalElements ?? 0);
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Could not load privileged accounts",
          pagErrorMessage(err, "Please retry in a moment."),
        );
    } finally {
      if (requestId === accountsRequestRef.current) {
        setAccountsLoading(false);
        setScopeLoading(false);
      }
    }
  }, [
    sourceSystemId,

    appliedSourceSystemIds,
    reloadToken,
    page,
    pageSize,
    appliedSearch,

    sortKey,
    sortDir,
    riskFilters,
    pamFilters,
    groupFilters,
    reviewFilters,
  ]);

  useEffect(() => {
    void loadAccounts();
  }, [loadAccounts]);

  const filteredAccounts = useMemo(() => {
    if (!accounts.length) return accounts;
    if (!groupFilters.length) return accounts;
    return accounts.filter((account) =>
      account.groupNames && Array.isArray(account.groupNames)
        ? account.groupNames.some((g) => groupFilters.includes(g))
        : false,
    );
  }, [accounts, groupFilters]);

  const visibleAccounts = useMemo(() => {
    if (!filteredAccounts.length) return filteredAccounts;
    if (sortKey === "groupNames") {
      return sortAccountsByGroup(filteredAccounts, sortDir);
    }
    return filteredAccounts;
  }, [filteredAccounts, sortKey, sortDir]);

  const displayedAccountsTotal = useMemo(() => {
    if (groupFilters.length > 0 && accounts.length > filteredAccounts.length) {
      return filteredAccounts.length;
    }
    return accountsTotal;
  }, [groupFilters, accounts, filteredAccounts, accountsTotal]);

  /* ── Dynamic Filter Options (from POST /accounts/filters) ───────── */
  const [filterOptionsData, setFilterOptionsData] = useState<PagAccountFiltersResponse | null>(null);
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false);

  useEffect(() => {
    if (!appliedSourceSystemIds.length) {
      setFilterOptionsData(null);
      return;
    }
    let cancelled = false;
    setFilterOptionsLoading(true);
    fetchPagAccountFilters(appliedSourceSystemIds)
      .then((data) => {
        if (!cancelled && data) {
          setFilterOptionsData(data);
        }
      })
      .catch(() => {
        // Fallback to accounts/chart-derived options if API call fails
      })
      .finally(() => {
        if (!cancelled) setFilterOptionsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [appliedSourceSystemIds, reloadToken]);

  /* ── Filter options (Powered by API /accounts/filters) ───────── */
  const pamFilterOptions = useMemo(() => {
    if (filterOptionsData?.pamStatuses?.length) {
      return filterOptionsData.pamStatuses.filter((s) => s && s.trim());
    }
    return [];
  }, [filterOptionsData]);

  const reviewFilterOptions = useMemo(() => {
    if (filterOptionsData?.reviewStatuses?.length) {
      return filterOptionsData.reviewStatuses
        .filter((status) => !REVIEW_STATUS_FILTERS_HIDDEN.includes(status))
        .map((status) => ({ value: status, label: reviewStatusLabel(status) }));
    }
    return [];
  }, [filterOptionsData]);

  const riskFilterOptions = useMemo(() => {
    if (filterOptionsData?.riskLevels?.length) {
      return filterOptionsData.riskLevels.map((lvl) => {
        const upper = lvl.toUpperCase();
        const value = upper === "UNASSIGNED" ? "UNCATEGORISED" : upper;
        const label =
          upper === "UNCATEGORISED" || upper === "UNASSIGNED"
            ? "Unassigned"
            : lvl.charAt(0).toUpperCase() + lvl.slice(1).toLowerCase();
        return { value, label };
      });
    }
    return [];
  }, [filterOptionsData]);

  const groupFilterOptions = useMemo(() => {
    if (filterOptionsData?.groupNames?.length) {
      return filterOptionsData.groupNames
        .filter((name) => name && name.trim())
        .map((name) => ({ value: name, label: name }));
    }
    return [];
  }, [filterOptionsData]);

  /* ── Source system change ──────────────────────────────────────── */
  const changeSourceSystems = (nextSystems: string[]) => {
    setSourceSystems(nextSystems);
    setResources((prevResources) => {
      if (nextSystems.length === 0) {
        return [];
      }
      const prevSystemSet = new Set(sourceSystems);
      const nextSystemSet = new Set(nextSystems);

      const addedSystems = nextSystems.filter((s) => !prevSystemSet.has(s));
      const removedSystems = sourceSystems.filter((s) => !nextSystemSet.has(s));

      const addedResourceIds = new Set(
        getResourceValuesForSystems(sourceSystemFilters, addedSystems),
      );
      const removedResourceIds = new Set(
        getResourceValuesForSystems(sourceSystemFilters, removedSystems),
      );

      const kept = prevResources.filter((id) => !removedResourceIds.has(id));
      const nextCombined = Array.from(new Set([...kept, ...addedResourceIds]));

      if (nextCombined.length === 0) {
        return getResourceValuesForSystems(sourceSystemFilters, nextSystems);
      }
      return nextCombined;
    });
  };

  /* ── Account detail dialog ─────────────────────────────────────── */
  const openAccountDetails = async (row: PagAccountRow) => {
    setDetailAccount(row);
    setDetailData(null);
    setDetailLoading(true);
    try {
      const details = await fetchPagAccountDetails(sourceSystemId, row.adUserId);
      setDetailData(details);
      if (details?.reviews?.length) {
        const latestWithDate = [...details.reviews].reverse().find((r) => r.dueDate);
        if (latestWithDate?.dueDate) {
          try {
            const stored = JSON.parse(localStorage.getItem("pag_account_due_dates") || "{}");
            stored[row.accountName] = latestWithDate.dueDate;
            if (row.adUserId) stored[String(row.adUserId)] = latestWithDate.dueDate;
            localStorage.setItem("pag_account_due_dates", JSON.stringify(stored));
          } catch {}
        }
      }
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          "Could not load the review details",
          pagErrorMessage(err, "Please retry in a moment."),
        );
    } finally {
      setDetailLoading(false);
    }
  };

  /* ── Submit access action ──────────────────────────────────────── */
  const submitAccessAction = async (action: AccessAction, payload: AccessActionPayload) => {
    setSubmittingAction(true);
    try {
      if (payload.dueDate) {
        try {
          const stored = JSON.parse(localStorage.getItem("pag_account_due_dates") || "{}");
          actionNames.forEach((name) => {
            stored[name] = payload.dueDate;
          });
          localStorage.setItem("pag_account_due_dates", JSON.stringify(stored));
        } catch {}
      }
      const result = await submitPagReviewAction({
        sourceSystemId,
        accountNames: actionNames,
        action: action === "review" ? "REVIEW" : "REVOKE",
        dueDate: payload.dueDate,
        justification: payload.justification,
        reviewedBy: useAuthStore.getState().user?.username ?? "",
      });
      setAccessAction(null);
      setSelectedIds([]);
      setReloadToken((token) => token + 1);
      useToastStore
        .getState()
        .showSuccess(
          result?.message ?? "Request submitted",
          [
            result?.reviewRef ? `Ref ${result.reviewRef}` : null,
            result?.results?.length
              ? result.results
                .map((entry) => `${entry.accountName}: ${entry.reviewLabel ?? entry.review}`)
                .join(", ")
              : `${actionNames.length} account(s) sent for ${action}.`,
          ]
            .filter(Boolean)
            .join(" - "),
        );
    } catch (err: unknown) {
      useToastStore
        .getState()
        .showError(
          action === "review" ? "Could not submit the review" : "Could not submit the revoke",
          pagErrorMessage(err, "Please retry in a moment."),
        );
    } finally {
      setSubmittingAction(false);
      setActionNames([]);
    }
  };

  /* ── Sort ───────────────────────────────────────────────────────── */
  const handleSort = (key: string) => {
    const next = cycleSortDir(sortKey, sortDir, key);
    setAccountsLoading(true);
    setSortKey(next.sortKey);
    setSortDir(next.sortDir);
    setPage(0);
  };

  /* ── Selection ─────────────────────────────────────────────────── */
  const selectableAccounts = useMemo(
    () => visibleAccounts.filter((row: PagAccountRow) => !hasOpenReviewRequest(row.reviewStatus)),
    [visibleAccounts],
  );
  const allSelected =
    selectableAccounts.length > 0 && selectedIds.length === selectableAccounts.length;
  const someSelected = selectedIds.length > 0 && !allSelected;

  const toggleRow = (name: string) =>
    setSelectedIds((prev) =>
      prev.includes(name) ? prev.filter((x) => x !== name) : [...prev, name],
    );

  const toggleAll = () =>
    setSelectedIds((prev) =>
      prev.length === selectableAccounts.length
        ? []
        : selectableAccounts.map((r: PagAccountRow) => r.accountName),
    );

  /* ── Return ────────────────────────────────────────────────────── */
  return {
    /* scope */
    sourceSystems,
    resources,
    sourceSystemFilters,
    appliedSourceSystemIds,
    setAppliedSourceSystemIds,
    stagedSourceSystemIds,
    insightsLoading,
    accountsLoading,
    scopeLoading,
    setScopeLoading,
    reloadToken,
    setReloadToken,
    setPage,
    setResources,
    changeSourceSystems,

    /* breakdowns */
    breakdown,
    privilegedAccountBreakdown,
    inventoryGroups,
    inventorySortKey,
    inventorySortDir,
    changeInventorySort,
    inventorySearch,
    setInventorySearch,
    inventorySearching,
    searchInventory,
    inventoryPage,
    inventoryPageSize,
    setInventoryPageSize,
    changeInventoryPageSize,
    inventoryLoading,
    changeInventoryPage,

    privilegedGroups,
    privilegedSortKey,
    privilegedSortDir,
    changePrivilegedSort,
    privilegedSearch,
    setPrivilegedSearch,
    privilegedSearching,
    searchPrivileged,
    privilegedPage,
    privilegedPageSize,
    setPrivilegedPageSize,
    changePrivilegedPageSize,
    privilegedLoading,
    changePrivilegedPage,

    inventoryTotal,
    privilegedTotal,
    privilegedPct,

    /* charts */
    accountsByGroup,
    riskDistribution,
    pamDistribution,
    openDrilldown,
    drilldown,
    setDrilldown,

    /* account table */
    search,

    setSearch: handleSearchChange,

    submitSearch,
    pamFilters,
    setPamFilters,
    reviewFilters,
    setReviewFilters,
    riskFilters,
    setRiskFilters,
    groupFilters,
    setGroupFilters,
    page,
    pageSize,
    appliedSearch,
    setPageSize,
    sortKey,
    sortDir,
    handleSort,
    visibleAccounts,
    accounts,
    accountsTotal: displayedAccountsTotal,
    pamFilterOptions,
    reviewFilterOptions,
    riskFilterOptions,
    groupFilterOptions,
    filterOptionsData,
    filterOptionsLoading,

    /* selection & actions */
    selectedIds,
    selectableAccounts,
    allSelected,
    someSelected,
    toggleRow,
    toggleAll,
    accessAction,
    setAccessAction,
    actionNames,
    submittingAction,
    openAccessAction,
    submitAccessAction,

    /* detail dialog */
    detailAccount,
    setDetailAccount,
    detailData,
    setDetailData,
    detailLoading,
    openAccountDetails,
  };
};
